/**
 * useMIDI Hook
 * Handles MIDI note input, recording, and clip operations
 */

import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  createMIDIEvent,
  MIDIEventType,
} from '../MIDIEngine';
import {
  createMIDIClip,
  midiEventsToClip,
  duplicateMIDIClip,
  moveMIDIClip,
  trimMIDIClip,
  splitMIDIClip,
  joinMIDIClips,
} from '../MIDIClip';
import { conditionalSnap } from '../GridSnap';

/**
 * Hook for MIDI operations
 */
export function useMIDI({
  audioContextRef,
  synthRef,
  drumSynthRef,
  tracks,
  setTracks,
  selectedTrack,
  pushUndoAction,
  ActionTypes,
  bpm,
  timeSignature,
  snapEnabled,
  countInEnabled,
  countInBeats,
  performCountIn,
  setIsCountingIn,
  setCountInProgress,
}) {
  // MIDI state
  const [activeNotes, setActiveNotes] = useState([]);
  const [activePads, setActivePads] = useState([]);
  const [isMidiRecording, setIsMidiRecording] = useState(false);
  const [midiRecordingEvents, setMidiRecordingEvents] = useState([]);
  
  // Piano Roll state
  const [showPianoRoll, setShowPianoRoll] = useState(false);
  const [editingClip, setEditingClip] = useState(null);
  const [editingTrackId, setEditingTrackId] = useState(null);
  
  // Refs
  const midiRecordStartTimeRef = useRef(0);
  
  // Handle MIDI note on (from keyboard)
  const handleMidiNoteOn = useCallback((note, velocity = 100) => {
    if (!synthRef?.current || !audioContextRef?.current) return;
    
    // Resume context if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    // Play note
    synthRef.current.noteOn(note, velocity);
    
    // Update active notes for UI
    setActiveNotes(prev => [...prev.filter(n => n !== note), note]);
    
    // If MIDI recording, capture event
    if (isMidiRecording && selectedTrack !== null) {
      const track = tracks.find(t => t.id === selectedTrack);
      if (track?.type === 'instrument') {
        const time = audioContextRef.current.currentTime - midiRecordStartTimeRef.current;
        const event = createMIDIEvent(MIDIEventType.NOTE_ON, note, velocity, time);
        setMidiRecordingEvents(prev => [...prev, event]);
      }
    }
  }, [synthRef, audioContextRef, isMidiRecording, selectedTrack, tracks]);
  
  // Handle MIDI note off (from keyboard)
  const handleMidiNoteOff = useCallback((note) => {
    if (!synthRef?.current) return;
    
    // Stop note
    synthRef.current.noteOff(note);
    
    // Update active notes for UI
    setActiveNotes(prev => prev.filter(n => n !== note));
    
    // If MIDI recording, capture note off
    if (isMidiRecording && selectedTrack !== null) {
      const track = tracks.find(t => t.id === selectedTrack);
      if (track?.type === 'instrument') {
        const time = audioContextRef.current.currentTime - midiRecordStartTimeRef.current;
        const event = createMIDIEvent(MIDIEventType.NOTE_OFF, note, 0, time);
        setMidiRecordingEvents(prev => [...prev, event]);
      }
    }
  }, [synthRef, audioContextRef, isMidiRecording, selectedTrack, tracks]);
  
  // Handle drum pad hit
  const handleDrumHit = useCallback((drumName, note, velocity = 100) => {
    if (!drumSynthRef?.current || !audioContextRef?.current) return;
    
    // Resume context if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    // Play drum sound
    drumSynthRef.current.playDrum(drumName, velocity);
    
    // Visual feedback
    setActivePads(prev => [...prev, drumName]);
    setTimeout(() => {
      setActivePads(prev => prev.filter(p => p !== drumName));
    }, 100);
    
    // If MIDI recording, capture event
    if (isMidiRecording && selectedTrack !== null) {
      const track = tracks.find(t => t.id === selectedTrack);
      if (track?.type === 'instrument' && track.instrumentType === 'drums') {
        const time = audioContextRef.current.currentTime - midiRecordStartTimeRef.current;
        const event = createMIDIEvent(MIDIEventType.NOTE_ON, note, velocity, time);
        setMidiRecordingEvents(prev => [...prev, event]);
      }
    }
  }, [drumSynthRef, audioContextRef, isMidiRecording, selectedTrack, tracks]);
  
  // Start MIDI recording
  const startMidiRecording = useCallback(async () => {
    const track = tracks.find(t => t.id === selectedTrack);
    if (!track || track.type !== 'instrument') {
      toast.error('Select an instrument track to record MIDI');
      return;
    }
    
    // Resume audio context
    if (audioContextRef?.current?.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    
    // Perform count-in if enabled
    if (countInEnabled && countInBeats > 0 && performCountIn) {
      setIsCountingIn?.(true);
      setCountInProgress?.(0);
      
      await performCountIn();
      
      setIsCountingIn?.(false);
      setCountInProgress?.(0);
    }
    
    // Start recording
    midiRecordStartTimeRef.current = audioContextRef.current.currentTime;
    setMidiRecordingEvents([]);
    setIsMidiRecording(true);
    
    toast.info('MIDI recording started');
  }, [selectedTrack, tracks, audioContextRef, countInEnabled, countInBeats, performCountIn, setIsCountingIn, setCountInProgress]);
  
  // Stop MIDI recording
  const stopMidiRecording = useCallback(() => {
    if (!isMidiRecording) return;
    
    setIsMidiRecording(false);
    
    const track = tracks.find(t => t.id === selectedTrack);
    if (!track) return;
    
    if (midiRecordingEvents.length === 0) {
      toast.info('No MIDI events recorded');
      return;
    }
    
    // Calculate duration from events
    const maxTime = midiRecordingEvents.reduce((max, e) => Math.max(max, e.time), 0);
    
    // Convert events to a MIDI clip
    const newClip = midiEventsToClip(midiRecordingEvents, selectedTrack);
    
    if (newClip) {
      // Adjust clip duration to snap to bars
      const secondsPerBeat = 60 / bpm;
      const beatsRecorded = Math.ceil(maxTime / secondsPerBeat);
      const barsRecorded = Math.ceil(beatsRecorded / timeSignature);
      newClip.duration = Math.max(secondsPerBeat * timeSignature, barsRecorded * timeSignature * secondsPerBeat);
      newClip.name = `${track.instrumentType === 'drums' ? 'Drum' : 'Synth'} Recording`;
    }
    
    // Save recording to track with undo support
    const oldMidiEvents = track.midiEvents || [];
    const oldMidiClips = track.midiClips || [];
    const newMidiEvents = [...oldMidiEvents, ...midiRecordingEvents];
    const newMidiClips = newClip ? [...oldMidiClips, newClip] : oldMidiClips;
    
    if (pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.RECORD_MIDI,
        trackId: selectedTrack,
        oldMidiEvents,
        newMidiEvents,
        oldMidiClips,
        newMidiClips,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === selectedTrack
        ? {
            ...t,
            midiEvents: newMidiEvents,
            midiClips: newMidiClips,
            duration: Math.max(t.duration, newClip?.duration || maxTime),
          }
        : t
    ));
    
    setMidiRecordingEvents([]);
    toast.success(`Recorded ${midiRecordingEvents.length} MIDI events`);
  }, [isMidiRecording, midiRecordingEvents, selectedTrack, tracks, pushUndoAction, ActionTypes, bpm, timeSignature, setTracks]);
  
  // Open clip in piano roll
  const openClipInPianoRoll = useCallback((clip, trackId) => {
    setEditingClip(clip);
    setEditingTrackId(trackId);
    setShowPianoRoll(true);
  }, []);
  
  // Close piano roll
  const closePianoRoll = useCallback(() => {
    setShowPianoRoll(false);
    setEditingClip(null);
    setEditingTrackId(null);
  }, []);
  
  // Handle piano roll clip update
  const handlePianoRollClipUpdate = useCallback((updatedClip, undoAction) => {
    if (!editingTrackId) return;
    
    setEditingClip(updatedClip);
    
    setTracks(prev => prev.map(t =>
      t.id === editingTrackId
        ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === updatedClip.id ? updatedClip : c) }
        : t
    ));
    
    if (undoAction && pushUndoAction && ActionTypes) {
      const track = tracks.find(t => t.id === editingTrackId);
      const oldClip = track?.midiClips?.find(c => c.id === updatedClip.id);
      
      pushUndoAction({
        type: ActionTypes[`PIANO_ROLL_${undoAction.type}`] || ActionTypes.UPDATE_MIDI_CLIP,
        trackId: editingTrackId,
        clipId: updatedClip.id,
        oldClip,
        newClip: updatedClip,
        ...undoAction,
      });
    }
  }, [editingTrackId, tracks, pushUndoAction, ActionTypes, setTracks]);
  
  // Move a MIDI clip
  const handleMoveClip = useCallback((clipId, newStartTime, createUndo = true) => {
    const track = tracks.find(t => t.midiClips?.some(c => c.id === clipId));
    if (!track) return;
    
    const clip = track.midiClips.find(c => c.id === clipId);
    if (!clip) return;
    
    const snappedStartTime = conditionalSnap(newStartTime, snapEnabled, bpm, timeSignature);
    const finalStartTime = Math.max(0, snappedStartTime);
    
    if (createUndo && pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.MOVE_MIDI_CLIP,
        trackId: track.id,
        clipId,
        oldStartTime: clip.startTime,
        newStartTime: finalStartTime,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === track.id
        ? { ...t, midiClips: t.midiClips.map(c => c.id === clipId ? moveMIDIClip(c, finalStartTime) : c) }
        : t
    ));
  }, [tracks, pushUndoAction, ActionTypes, snapEnabled, bpm, timeSignature, setTracks]);
  
  // Resize a MIDI clip
  const handleResizeClip = useCallback((clipId, newDuration, createUndo = true) => {
    const track = tracks.find(t => t.midiClips?.some(c => c.id === clipId));
    if (!track) return;
    
    const clip = track.midiClips.find(c => c.id === clipId);
    if (!clip) return;
    
    const endTime = clip.startTime + newDuration;
    const snappedEndTime = conditionalSnap(endTime, snapEnabled, bpm, timeSignature);
    const snappedDuration = Math.max(0.25, snappedEndTime - clip.startTime);
    
    if (createUndo && pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.RESIZE_MIDI_CLIP,
        trackId: track.id,
        clipId,
        oldDuration: clip.duration,
        newDuration: snappedDuration,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === track.id
        ? { ...t, midiClips: t.midiClips.map(c => c.id === clipId ? trimMIDIClip(c, { newDuration: snappedDuration }) : c) }
        : t
    ));
  }, [tracks, pushUndoAction, ActionTypes, snapEnabled, bpm, timeSignature, setTracks]);
  
  // Delete a MIDI clip
  const handleDeleteClip = useCallback((clipId) => {
    const track = tracks.find(t => t.midiClips?.some(c => c.id === clipId));
    if (!track) return;
    
    const clip = track.midiClips.find(c => c.id === clipId);
    if (!clip) return;
    
    if (pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.REMOVE_MIDI_CLIP,
        trackId: track.id,
        clipId,
        clip,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === track.id
        ? { ...t, midiClips: t.midiClips.filter(c => c.id !== clipId) }
        : t
    ));
    
    if (editingClip?.id === clipId) {
      closePianoRoll();
    }
    
    toast.success('Clip deleted');
  }, [tracks, pushUndoAction, ActionTypes, editingClip, closePianoRoll, setTracks]);
  
  // Duplicate a MIDI clip
  const handleDuplicateClip = useCallback((clipId) => {
    const track = tracks.find(t => t.midiClips?.some(c => c.id === clipId));
    if (!track) return;
    
    const clip = track.midiClips.find(c => c.id === clipId);
    if (!clip) return;
    
    const newClip = duplicateMIDIClip(clip);
    
    if (pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.DUPLICATE_MIDI_CLIP,
        trackId: track.id,
        clipId,
        newClipId: newClip.id,
        newClip,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === track.id
        ? { ...t, midiClips: [...t.midiClips, newClip] }
        : t
    ));
    
    toast.success('Clip duplicated');
  }, [tracks, pushUndoAction, ActionTypes, setTracks]);
  
  // Create empty MIDI clip
  const createEmptyMidiClip = useCallback((trackId, startTime = 0) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track || track.type !== 'instrument') {
      toast.error('Select an instrument track');
      return;
    }
    
    const secondsPerBar = (60 / bpm) * timeSignature;
    const newClip = createMIDIClip({
      trackId,
      startTime,
      duration: secondsPerBar,
      name: `${track.instrumentType === 'drums' ? 'Drum' : 'Synth'} Clip`,
    });
    
    if (pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.ADD_MIDI_CLIP,
        trackId,
        clipId: newClip.id,
        clip: newClip,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === trackId
        ? { ...t, midiClips: [...(t.midiClips || []), newClip] }
        : t
    ));
    
    toast.success('New MIDI clip created');
    return newClip;
  }, [tracks, bpm, timeSignature, pushUndoAction, ActionTypes, setTracks]);
  
  // Split a MIDI clip
  const handleSplitMidiClip = useCallback((trackId, clipId, splitTime) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const clip = track.midiClips?.find(c => c.id === clipId);
    if (!clip) return;
    
    const snappedSplitTime = conditionalSnap(splitTime, snapEnabled, bpm, timeSignature);
    const result = splitMIDIClip(clip, snappedSplitTime);
    
    if (!result) {
      toast.error('Cannot split at this position');
      return;
    }
    
    const [leftClip, rightClip] = result;
    
    if (pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.SPLIT_MIDI_CLIP,
        trackId,
        originalClipId: clipId,
        originalClip: clip,
        leftClip,
        rightClip,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === trackId
        ? {
            ...t,
            midiClips: [
              ...(t.midiClips || []).filter(c => c.id !== clipId),
              leftClip,
              rightClip,
            ]
          }
        : t
    ));
    
    if (editingClip?.id === clipId) {
      setShowPianoRoll(false);
      setEditingClip(null);
      setEditingTrackId(null);
    }
    
    toast.success('Clip split');
  }, [tracks, pushUndoAction, ActionTypes, editingClip, snapEnabled, bpm, timeSignature, setTracks]);
  
  // Join MIDI clips
  const handleJoinMidiClips = useCallback((trackId, clipIds) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const clipsToJoin = clipIds
      .map(id => track.midiClips?.find(c => c.id === id))
      .filter(Boolean);
    
    if (clipsToJoin.length < 2) {
      toast.error('Select at least 2 clips to join');
      return;
    }
    
    const mergedClip = joinMIDIClips(clipsToJoin);
    if (!mergedClip) {
      toast.error('Failed to join clips');
      return;
    }
    
    if (pushUndoAction && ActionTypes) {
      pushUndoAction({
        type: ActionTypes.JOIN_MIDI_CLIPS,
        trackId,
        originalClipIds: clipIds,
        originalClips: clipsToJoin,
        mergedClip,
      });
    }
    
    setTracks(prev => prev.map(t =>
      t.id === trackId
        ? {
            ...t,
            midiClips: [
              ...(t.midiClips || []).filter(c => !clipIds.includes(c.id)),
              mergedClip,
            ]
          }
        : t
    ));
    
    if (editingClip && clipIds.includes(editingClip.id)) {
      setShowPianoRoll(false);
      setEditingClip(null);
      setEditingTrackId(null);
    }
    
    toast.success('Clips joined');
  }, [tracks, pushUndoAction, ActionTypes, editingClip, setTracks]);
  
  return {
    // State
    activeNotes,
    setActiveNotes,
    activePads,
    setActivePads,
    isMidiRecording,
    midiRecordingEvents,
    showPianoRoll,
    setShowPianoRoll,
    editingClip,
    setEditingClip,
    editingTrackId,
    setEditingTrackId,
    
    // Methods
    handleMidiNoteOn,
    handleMidiNoteOff,
    handleDrumHit,
    startMidiRecording,
    stopMidiRecording,
    openClipInPianoRoll,
    closePianoRoll,
    handlePianoRollClipUpdate,
    handleMoveClip,
    handleResizeClip,
    handleDeleteClip,
    handleDuplicateClip,
    createEmptyMidiClip,
    handleSplitMidiClip,
    handleJoinMidiClips,
    
    // Additional exports for external MIDI device integration
    setMidiRecordingEvents,
    midiRecordStartTimeRef,
  };
}

export default useMIDI;
